import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { signalRService } from '../services/signalrService';
import { apiConfig } from '../config/authConfig';
import ProgressIndicator from './ProgressIndicator';
import ConfirmationModal from './ConfirmationModal';
import { ShopifyProduct, shopifyService } from '../services/shopifyService';

// Temporary inline types to resolve import issues
interface ProductMetadata {
  name: string;
  description: string;
  price: number;
  currency: string;
  category: string;
  materials: string[];
  dimensions: {
    height: number;
    width: number;
    depth: number;
    weight: number;
  };
  tags: string[];
}

interface MultiLanguageMetadata {
  de: ProductMetadata;
  en: ProductMetadata;
  fr: ProductMetadata;
  es: ProductMetadata;
}

interface RefinedProductData {
  refinedMetadata: MultiLanguageMetadata;
  sharedInformation: Record<string, unknown>;
  originalData: Record<string, unknown>;
  correlationId: string;
  agentSummary: string;
  shopify_product_id?: string;
  images?: string[];
}

interface ProductListProps {
  onRefineProduct: (productData: RefinedProductData) => void;
}

// Global flag to prevent multiple SignalR connections across component instances
let globalSignalRInitialized = false;

// Reset flag in test environment
if (process.env.NODE_ENV === 'test') {
  globalSignalRInitialized = false;
}

/**
 * Parse tags from API response - handles both string and array formats
 * Shopify API returns tags as comma-separated string, but frontend expects array
 */
const parseTags = (tags: string | string[] | null | undefined): string[] => {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags;
  if (typeof tags === 'string' && tags.trim()) {
    return tags.split(',').map(tag => tag.trim()).filter(Boolean);
  }
  return [];
};

const ProductList: React.FC<ProductListProps> = ({ onRefineProduct }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refining, setRefining] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<ShopifyProduct | null>(null);
  const [connectionId, setConnectionId] = useState<string>('');
  const [refineCorrelationId, setRefineCorrelationId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [nextPageInfo, setNextPageInfo] = useState<string | null>(null);
  const signalRInitialized = useRef(false);

  const fetchProducts = useCallback(async (pageCursor?: string | null, append = false) => {
    const limit = 50;
    const cursor = pageCursor ?? null;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await shopifyService.getProducts(limit, searchQuery, cursor ?? undefined);

      if (response.success) {
        setProducts(prev => append ? [...prev, ...response.products] : response.products);
        setNextPageInfo(response.next_page_info ?? null);
      } else {
        const message = response.error || 'Produkte konnten nicht geladen werden.';
        toast.error(message);
        if (!append) {
          setProducts([]);
          setNextPageInfo(null);
        }
      }
    } catch (error) {
      console.error('Failed to load products:', error);
      toast.error('Produkte konnten nicht geladen werden.');
      if (!append) {
        setProducts([]);
        setNextPageInfo(null);
      }
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [searchQuery]);

  const filteredProducts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return products;
    }

    return products.filter(product => {
      const sku = product.variants?.[0]?.sku ?? '';
      const matchSources = [
        product.title,
        product.product_type,
        product.vendor,
        product.handle,
        sku,
        product.body_html || '',
        parseTags(product.tags).join(' ')
      ];

      return matchSources.some(source =>
        typeof source === 'string' && source.toLowerCase().includes(query)
      );
    });
  }, [products, searchQuery]);

  // Initialize SignalR connection once on mount
  useEffect(() => {
    let isMounted = true;
    const initializeSignalR = async () => {
      if (globalSignalRInitialized || signalRInitialized.current) {
        return; // Already initialized globally or locally
      }
      
      try {
        const connId = await signalRService.connect();
        if (isMounted) {
          setConnectionId(connId);
          signalRInitialized.current = true;
          globalSignalRInitialized = true;
          // console.log('ProductList SignalR connected with ID:', connId);
        }
      } catch (error) {
        console.error('Failed to connect to SignalR:', error);
        if (isMounted) {
          setConnectionId(`conn-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
          signalRInitialized.current = true;
          globalSignalRInitialized = true;
        }
      }
    };

    initializeSignalR();
    
    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array - only run once on mount

  // Fetch products when dependencies change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProducts();
    }, 500);
    return () => clearTimeout(timer);
  }, [fetchProducts, searchQuery]);

  const handleProductClick = useCallback((product: ShopifyProduct) => {
    if (!product || !product.id) {
      return;
    }
    navigate(`/products/${product.id}`);
  }, [navigate]);

  const handleLoadMore = () => {
    if (nextPageInfo) {
      fetchProducts(nextPageInfo, true);
    }
  };

  const convertImageToBase64 = async (imageUrl: string): Promise<string> => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Failed to convert image to base64:', error);
      throw error;
    }
  };

  const handleRefine = async (product: ShopifyProduct) => {
    setRefining(product.id);
    
    try {
      // Load product images as base64
      const images: string[] = [];
      for (const image of (product.images || []).slice(0, 5)) { // Limit to 5 images
        try {
          const base64Image = await convertImageToBase64(image.src);
          images.push(base64Image);
        } catch (error) {
          console.warn(`Failed to load image ${image.src}:`, error);
        }
      }

      // Convert Shopify product to our format for refinement
      const productData = {
        title: product.title,
        product_type: product.product_type,
        vendor: product.vendor,        tags: parseTags(product.tags),
        price: parseFloat(product.variants?.[0]?.price || '0'),
        sku: product.variants?.[0]?.sku || '',
        inventory_quantity: product.variants?.[0]?.inventory_quantity || 0,
        weight: product.variants?.[0]?.weight || 0,
        dimensions: product.metafields?.dimensions || null,        materials: product.metafields?.materials || [],
        colors: product.metafields?.colors || [],
        shopify_product_id: product.id,
        handle: product.handle,
        images: images // Include the loaded images
      };

      // Get auth headers from shopifyService
      const authHeaders = await shopifyService.getAuthHeadersPublic();
      
      const refineResponse = await fetch(`${apiConfig.baseUrl}/refine`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'X-Connection-Id': connectionId,
        },
        body: JSON.stringify({ productData }),
      });

      if (refineResponse.ok) {
        const result = await refineResponse.json();
        setRefineCorrelationId(result.correlationId);
        toast.success('Produkt erfolgreich verfeinert! Weiterleitung zum Bearbeitungsformular...');
        
        // Pass refined data to parent component with shopify_id for updates
        onRefineProduct({
          refinedMetadata: result.refinedMetadata,
          sharedInformation: result.sharedInformation,
          originalData: result.originalData,
          correlationId: result.correlationId,
          agentSummary: result.agentSummary,
          shopify_product_id: product.id, // Include Shopify ID for updates
          images: images // Include images for the form
        });
      } else {
        const error = await refineResponse.json();
        toast.error(`Verfeinerung fehlgeschlagen: ${error.error || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      console.error('Refinement failed:', error);
      toast.error('Verfeinerung fehlgeschlagen');
    } finally {
      setRefining(null);
    }
  };

  const handleDeleteClick = (product: ShopifyProduct) => {
    setProductToDelete(product);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;

    setDeleting(productToDelete.id);
    try {
      const result = await shopifyService.deleteProduct(productToDelete.id);
      
      if (result.success) {
        // Remove the product from the local state
        setProducts(prevProducts => prevProducts.filter(p => p.id !== productToDelete.id));
        toast.success(`"${productToDelete.title}" wurde erfolgreich gelöscht`);
      } else {
        toast.error(`Löschen fehlgeschlagen: ${result.error || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Löschen fehlgeschlagen');
    } finally {
      setDeleting(null);
      setDeleteModalOpen(false);
      setProductToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setProductToDelete(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getInventoryColor = (quantity: number) => {
    if (quantity === 0) return 'text-red-600';
    if (quantity <= 2) return 'text-yellow-600';
    return 'text-green-600';
  };

  if (loading && products.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-clay-600"></div>
        <span className="ml-3 text-gray-600">Lade Produkte...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold leading-7 text-pottery-earth sm:text-3xl">
              Shopify Produktkatalog
            </h2>
            <p className="mt-1 text-sm text-clay-600">
              Verwalten Sie Ihre Töpferei-Produkte und verfeinern Sie sie mit KI-Unterstützung.
            </p>
          </div>
          <div className="w-full sm:w-80">
            <label htmlFor="product-search" className="block text-sm font-medium text-gray-700">
              Produkte durchsuchen
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <input
                id="product-search"
                type="search"
                placeholder="Titel, Kategorie, SKU oder Tags"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="focus:ring-clay-500 focus:border-clay-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 pl-3 pr-10"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-sm text-gray-500 hover:text-gray-700"
                >
                  Zurücksetzen
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {filteredProducts && filteredProducts.length === 0 && !loading ? (
            <li className="px-4 py-8 text-center text-gray-500 text-sm">
              Keine Produkte gefunden. Keine Produkte entsprechen Ihrer Suche.
            </li>
          ) : (
            filteredProducts?.map((product) => {
              const primaryVariant = product.variants?.[0];
              const inventoryQuantity = typeof primaryVariant?.inventory_quantity === 'number'
                ? primaryVariant.inventory_quantity
                : 0;
              const sku = primaryVariant?.sku || 'N/A';
              const updatedDate = formatDate(product.updated_at || product.created_at);

              return (
                <li key={product.id} className="hover:bg-gray-50 transition-colors">
                <div className="px-4 py-4 sm:px-6">
                  <div
                    className="cursor-pointer space-y-4"
                    onClick={() => handleProductClick(product)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 h-16 w-16 sm:h-20 sm:w-20">
                        {product.images.length > 0 ? (
                          <img
                            className="h-16 w-16 sm:h-20 sm:w-20 rounded-lg object-cover"
                            src={product.images[0].src}
                            alt={product.images[0].alt}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMCAyMEg0NFY0NEgyMFYyMFoiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPHBhdGggZD0iTTI4IDI4TDM2IDM2TDQ0IDI4IiBzdHJva2U9IiM5Q0EzQUYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+Cjwvc3ZnPgo=';
                            }}
                          />
                        ) : (
                          <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-lg bg-gray-200 flex items-center justify-center">
                            <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-base sm:text-lg font-medium text-pottery-earth line-clamp-2">
                            {product.title}
                          </p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(product.status)}`}>
                            {product.status === 'active' ? 'Aktiv' : product.status === 'draft' ? 'Entwurf' : 'Archiviert'}
                          </span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-gray-500">
                          <span className="font-semibold text-pottery-earth">€{product.variants?.[0]?.price || '0.00'}</span>
                          {product.vendor && (
                            <>
                              <span className="hidden sm:inline">•</span>
                              <span>{product.vendor}</span>
                            </>
                          )}
                          {product.product_type && (
                            <>
                              <span className="hidden sm:inline">•</span>
                              <span>{product.product_type}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                        <div className="flex flex-wrap items-center gap-2">
                      <a
                        href={`https://admin.shopify.com/store/${apiConfig.shopifyStoreHandle}/products/${product.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-clay-500"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="flex items-center gap-1">
                          Shopify
                          <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0h-11m11 0v11" />
                          </svg>
                        </span>
                      </a>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRefine(product); }}
                        disabled={refining === product.id || deleting === product.id}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-clay-600 hover:bg-clay-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-clay-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {refining === product.id ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-1 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            ...
                          </>
                        ) : (
                          'Verfeinern'
                        )}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(product); }}
                        disabled={refining === product.id || deleting === product.id}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Produkt löschen"
                      >
                        {deleting === product.id ? (
                          <>
                            <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          </>
                        ) : (
                          'Löschen'
                        )}
                      </button>
                    </div>

                    <div className="mt-3">
                      <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-500">
                        <span>{product.product_type || product.vendor || 'Produkt'} • SKU: {sku}</span>
                        <span className="hidden sm:inline">•</span>
                        <br className="sm:hidden" />
                        <span className={`font-medium ${getInventoryColor(inventoryQuantity)}`}>
                          {inventoryQuantity} auf Lager
                        </span>
                        <span className="hidden sm:inline">•</span>
                        <br className="sm:hidden" />
                        <span>Aktualisiert: {updatedDate}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 sm:gap-2">
                        {parseTags(product.tags).slice(0, 4).map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-clay-100 text-clay-800"
                          >
                            {tag}
                          </span>
                        ))}
                        {parseTags(product.tags).length > 4 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                            +{parseTags(product.tags).length - 4} weitere
                          </span>
                        )}
                      </div>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                        <div className="flex sm:block gap-1">
                          <span className="font-medium">Abmessungen:</span>
                          <span className="sm:block">
                            {product.metafields?.dimensions ?
                              `${product.metafields.dimensions.height_cm}×${product.metafields.dimensions.width_cm}×${product.metafields.dimensions.depth_cm} cm` :
                              'N/A'}
                          </span>
                        </div>
                        <div className="flex sm:block gap-1">
                          <span className="font-medium">Materialien:</span>
                          <span className="sm:block">
                            {product.metafields?.materials?.length ?
                              product.metafields.materials.join(', ') :
                              'N/A'}
                          </span>
                        </div>
                        <div className="flex sm:block gap-1">
                          <span className="font-medium">Farben:</span>
                          <span className="sm:block">
                            {product.metafields?.colors?.length ?
                              product.metafields.colors.join(', ') :
                              'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </li>
              );
            })
          )}
        </ul>
        {nextPageInfo && (
          <div className="flex justify-center mt-6 mb-8">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className={`px-4 py-2 border border-gray-300 rounded-md text-sm font-medium ${
                loadingMore
                  ? 'bg-gray-100 text-gray-400 cursor-wait'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {loadingMore ? 'Lade weitere Produkte…' : 'Mehr Produkte laden'}
            </button>
          </div>
        )}
      </div>
      {/* Progress Indicator */}
      <ProgressIndicator 
        correlationId={refineCorrelationId || undefined}
        onComplete={() => {
          setRefineCorrelationId(null);
        }}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Produkt löschen"
        message={`Möchten Sie das Produkt "${productToDelete?.title || ''}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
        confirmText="Löschen"
        cancelText="Abbrechen"
        variant="danger"
        isLoading={deleting !== null}
      />
    </div>
  );
};

export default ProductList;